import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { MatSidenav } from "@angular/material/sidenav";
import { Router, RouterModule } from "@angular/router";
import { HeaderModule, SidebarModule } from "@coreui/angular";
import { NgScrollbarModule } from "ngx-scrollbar";
import {
  PERMISSIONS,
  ReviewStatus,
  ROLE_RESOURCE_PERMISSIONS,
  STATIC_KEYS,
  USER_ROLE,
} from "src/constants/constants";
import { SharedAppModule } from "src/pages/shared/shared.module";
import { CommonService } from "src/services/common.service";
import { HeaderComponent } from "../header/header.component";
import { navItems } from "./_nav";
import { ApiService } from "src/services/api.service";
import { forkJoin } from "rxjs";
import { NavService } from "src/services/nav.service";

@Component({
  selector: "app-left-section",
  standalone: true,
  imports: [
    SidebarModule,
    RouterModule,
    NgScrollbarModule,
    CommonModule,
    HeaderModule,
    HeaderComponent,
    SharedAppModule,
  ],
  templateUrl: "./left-section.component.html",
  styleUrls: ["./left-section.component.scss"],
})
export class LeftSectionComponent implements OnInit {
  isOpen: boolean = true;
  public navItems = [...navItems];
  sidebarVisible = true;
  isWebView!: boolean;
  activeParent: string = "";
  activeChild: string = "";

  @ViewChild(MatSidenav)
  sidenav!: MatSidenav;

  constructor(
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private apiService: ApiService,
    private navService: NavService
  ) {
    // Removed call to handleSelectedTab() from the constructor
  }

  ngOnInit() {
    this.isWebView = this.commonService.isWebView();
    this.updateNavItems();
    this.navService.navItems$.subscribe((items) => {
      this.navItems = items;
    });
    // Clear active selections on reload
    this.activeParent = "";
    this.activeChild = "";
    this.getSideCount();
    this.handleSelectedTab();
  }

  getSideCount() {
    const role = this.commonService.getItem(
      STATIC_KEYS.LOGGED_IN_USER_INFO,
      true
    ).roles?.[0]?.slug;
    if (role === USER_ROLE.COMPUTE_APPROVER) return;

    const approvalList$ = this.apiService.getApprovalList(
      [
        {
          key: "status",
          value: JSON.stringify(
            role === USER_ROLE.DATA_ONBOARDING_APPROVER
              ? [ReviewStatus.Pending]
              : [ReviewStatus.LevelApproved, ReviewStatus.Pending]
          ),
        },
      ],
      false
    );

    const reportArtifacts$ = this.apiService.getGrevCount();
    // Add the API in forkJoin to add count
    forkJoin([approvalList$, reportArtifacts$]).subscribe(
      ([approvalRes, grevCountObj]) => {
        if (approvalRes.header.success) {
          const approvalCount = approvalRes.data?.orgList?.length || approvalRes.data?.length || 0;

          const grevCount =
            (grevCountObj?.data?.disputedArtefactCount || 0) +
            (grevCountObj?.data?.generalGrievanceCount || 0);
          this.navService.updateBadge(
            ROLE_RESOURCE_PERMISSIONS.ORG_APPROVAL,
            approvalCount
          );
          this.navService.updateBadge(
            ROLE_RESOURCE_PERMISSIONS.MANAGE_GRIEVANCES,
            grevCount || 0
          );
        }
      }
    );
    this.navService.updateDatasetCount();
  }

  handleVisible(event: boolean) {
    this.commonService.toggleSidebar(event);
  }

  toggleClick() {
    this.sidenav.toggle();
    this.isOpen = !this.isOpen;
  }

  toggleParent(name: string) {
    this.activeParent = name;
    this.activeChild = "";
  }

  toggleParentChild(name: string) {
    this.activeChild = name;
    this.cdRef.detectChanges();
  }

  toggleDropdown(item: any) {
    item.expanded = !item.expanded;
  }

  navigateTo(url: string) {
    this.router.navigate([url]);
  }

  handleSelectedTab() {
    const currentUrl = this.router.url;

    // Clear active values before processing
    this.activeParent = "";
    this.activeChild = "";

    for (const navItem of this.navItems) {
      const { children, name, url } = navItem;
      // Check if this nav item matches the current URL.
      if (`/${url}` === currentUrl) {
        this.activeParent = name || "";
        this.cdRef.detectChanges();
        break;
      }

      // Check children if available
      if (children?.length) {
        const child = children.find(
          ({ url: childUrl }) => childUrl === currentUrl
        );
        if (child) {
          this.activeParent = name || "";
          this.activeChild = child.name || "";
          this.cdRef.detectChanges();
          break;
        }
      }
    }
    this.updateNavItems();
  }

  private getFilteredNavItems(userPermissions: any = {}) {
    const permissionKeys = Object.keys(userPermissions);

    if (permissionKeys.includes(PERMISSIONS.ALL)) {
      return this.navItems.map((item) => ({
        ...item,
        ...(item.children?.length
          ? { expanded: this.activeParent === item.name }
          : {}),
      }));
    }

    if (permissionKeys.includes("r-all")) {
      return this.navItems
        .map((item) => ({
          ...item,
          ...(item.children?.length
            ? { expanded: this.activeParent === item.name }
            : {}),
        }))
        .filter(({ name }) => name !== "Role Management");
    }

    const permissionSet = new Set(permissionKeys);

    const data = this.navItems.map((item) => {
      const filteredChildren = item.children?.filter(
        (child) => !child.class || permissionSet.has(child.class)
      );

      const hasPermission =
        !item.class ||
        permissionSet.has(item.class) ||
        filteredChildren?.length;

      if (!hasPermission) return null;
      if (!item.children?.length) {
        return item;
      }
      return filteredChildren?.length
        ? {
            ...item,
            children: filteredChildren,
            expanded: this.activeParent === item.name,
          }
        : null;
    });

    return data.filter(Boolean);
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = "assets/images/homeIcon.svg";
  }

  private updateNavItems() {
    const storedPermissions = this.commonService.getPermissions();
    const filtered = this.getFilteredNavItems(storedPermissions);
    this.navService.setNavItems(filtered);
  }
}
